import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import screens from './Screens.module.css';
import styles from './Pricing.module.css';
import { Button, Modal, ModalRow, Spinner, TierCard } from '../../components/ui';
import { getPaymentStatus, getPricing, isPaymentSettled, subscribe } from '../../api/payments.js';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useI18n } from '../../i18n/index.jsx';

/*
 * Plans and checkout. Fully wired to the live backend.
 *
 * Price integrity: every amount shown for a paid tier comes from
 * GET /api/web/pricing. This screen never contains a hardcoded price and never
 * sends one — POST /api/web/subscribe/{tier} names a tier only, and the server
 * decides what it costs.
 *
 * Checkout: the backend returns a NOWPayments hosted invoice_url. We send the
 * user there and remember the payment id, so that when they come back we can
 * poll GET /api/web/payment/{id}/status and activate the UI as soon as the
 * network confirms.
 */

const PENDING_PAYMENT_KEY = 'dq_pending_payment';

function readPendingPayment() {
  try {
    const raw = localStorage.getItem(PENDING_PAYMENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writePendingPayment(value) {
  try {
    if (value) localStorage.setItem(PENDING_PAYMENT_KEY, JSON.stringify(value));
    else localStorage.removeItem(PENDING_PAYMENT_KEY);
  } catch {
    /* ignore */
  }
}

export function Pricing() {
  const { t } = useI18n();
  const { subscription, tier: currentTier, isPaid, refreshSubscription } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [pricing, setPricing] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [checkout, setCheckout] = useState(null); // the tier being purchased
  const [starting, setStarting] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');

  // Payment we're waiting on: {id, tier}
  const [pending, setPending] = useState(readPendingPayment);
  const [pendingStatus, setPendingStatus] = useState('');
  const [settledMessage, setSettledMessage] = useState('');

  useEffect(() => {
    document.title = `${t('pricing_title')} — Don Quack`;
  }, [t]);

  const loadPricing = useCallback(async () => {
    setLoadError('');
    try {
      setPricing(await getPricing());
    } catch (err) {
      setLoadError(err.message || t('error_generic'));
    }
  }, [t]);

  useEffect(() => {
    loadPricing();
  }, [loadPricing]);

  // Coming back from the hosted checkout: the id may be in the URL, otherwise
  // we use the one stashed before redirecting.
  useEffect(() => {
    const fromUrl = searchParams.get('payment_id');
    if (fromUrl && !pending) {
      const next = { id: fromUrl, tier: searchParams.get('tier') || '' };
      setPending(next);
      writePendingPayment(next);
      searchParams.delete('payment_id');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, pending]);

  /* ---- poll a pending payment until it settles ---- */
  const pollTimer = useRef(null);

  useEffect(() => {
    if (!pending?.id) return undefined;

    let cancelled = false;
    let attempts = 0;

    async function poll() {
      attempts += 1;
      try {
        const res = await getPaymentStatus(pending.id);
        if (cancelled) return;

        const status = res.payment?.status ?? '';
        setPendingStatus(status);

        if (isPaymentSettled(status)) {
          setPending(null);
          writePendingPayment(null);
          setSettledMessage(
            status === 'confirmed' ? t('payment_confirmed') : t('payment_not_completed')
          );
          if (status === 'confirmed') refreshSubscription();
          return;
        }
      } catch {
        // A failed poll isn't fatal — try again on the next tick.
      }

      // Back off: every 5s at first, then every 30s. Give up after ~10 minutes
      // so a tab left open doesn't poll forever; the user can check manually.
      if (!cancelled && attempts < 40) {
        pollTimer.current = setTimeout(poll, attempts < 12 ? 5000 : 30000);
      }
    }

    poll();

    return () => {
      cancelled = true;
      clearTimeout(pollTimer.current);
    };
  }, [pending, refreshSubscription, t]);

  /* ---- start a purchase ---- */
  async function startCheckout(tierId) {
    setStarting(true);
    setCheckoutError('');
    try {
      const res = await subscribe(tierId);
      const paymentId = res.payment?.payment_id;

      if (!res.invoice_url) throw new Error(t('error_generic'));

      if (paymentId) {
        const next = { id: paymentId, tier: tierId };
        setPending(next);
        writePendingPayment(next);
      }

      // Hand off to NOWPayments' hosted page — we never touch payment details.
      window.location.assign(res.invoice_url);
    } catch (err) {
      setCheckoutError(err.message || t('error_generic'));
      setStarting(false);
    }
  }

  const periodDays = pricing?.period_days ?? 30;

  return (
    <section>
      <h2 className={screens.title}>{t('pricing_title')}</h2>
      <p className={screens.sub}>{t('pricing_subtitle')}</p>

      {settledMessage && (
        <div className={settledMessage === t('payment_confirmed') ? screens.noticeBox : screens.errorBox}>
          {settledMessage}
        </div>
      )}

      {pending && (
        <div className={styles.pending}>
          <Spinner size={26} />
          <div>
            <h3>{t('payment_waiting_title')}</h3>
            <p className="muted">{t('payment_waiting_body')}</p>
            {pendingStatus && (
              <p className={styles.pendingStatus}>
                {t('payment_still_pending')} ({pendingStatus})
              </p>
            )}
          </div>
        </div>
      )}

      {loadError && (
        <div className={screens.errorBox}>
          <span>{loadError}</span>
          <Button size="sm" variant="secondary" onClick={loadPricing}>
            {t('retry')}
          </Button>
        </div>
      )}

      {!pricing && !loadError ? (
        <div className={screens.loadingBlock}>
          <Spinner />
        </div>
      ) : (
        <div className={styles.tiers}>
          {/* Free isn't a product the server sells, it's the absence of a paid
              plan — so it's rendered from copy, not from the pricing endpoint. */}
          <TierCard
            name={t('plan_free')}
            price={t('plan_free_price')}
            period=""
            features={[
              t('plan_free_feature_1'),
              t('plan_free_feature_2'),
              t('plan_free_feature_3'),
            ]}
            current={!isPaid}
            ctaLabel={!isPaid ? t('plan_current') : t('plan_free')}
          />

          {pricing?.tiers?.map((tierData) => {
            const isCurrent = isPaid && currentTier === tierData.tier;
            return (
              <TierCard
                key={tierData.tier}
                name={tierData.name}
                /* Straight from the server. Never computed or hardcoded here. */
                price={formatPrice(tierData.price_usd, tierData.currency)}
                period={t('plan_period_month')}
                features={tierFeatures(tierData, t)}
                featured={tierData.tier === 'premium'}
                ribbon={t('most_popular')}
                current={isCurrent}
                ctaLabel={
                  isCurrent ? t('plan_current') : t('plan_choose', { plan: tierData.name })
                }
                onCta={() => {
                  setCheckoutError('');
                  setCheckout(tierData);
                }}
              />
            );
          })}
        </div>
      )}

      <p className={styles.note}>{t('pricing_note')}</p>

      {isPaid && subscription?.expires_at && (
        <p className={styles.expiry}>
          {t('account_expires')}:{' '}
          {new Date(subscription.expires_at).toLocaleDateString()}
          {subscription.days_remaining !== null &&
            ` · ${t('account_days_remaining', { days: subscription.days_remaining })}`}
        </p>
      )}

      {/* ---- checkout confirmation ---- */}
      <Modal
        open={Boolean(checkout)}
        onClose={() => !starting && setCheckout(null)}
        title={checkout ? t('checkout_title', { plan: checkout.name }) : ''}
      >
        {checkout && (
          <>
            <p className="muted" style={{ fontSize: 13, marginBottom: 14 }}>
              {t('checkout_intro')}
            </p>

            <ModalRow label={t('checkout_plan')} value={checkout.name} />
            <ModalRow
              label={t('checkout_total')}
              value={formatPrice(checkout.price_usd, checkout.currency)}
            />
            <ModalRow
              label={t('checkout_period_label')}
              value={t('checkout_period', { days: periodDays })}
            />

            <p className="muted" style={{ fontSize: 13, margin: '18px 0' }}>
              {t('checkout_redirect_note')}
            </p>

            {checkoutError && <div className={screens.errorBox}>{checkoutError}</div>}

            <Button block loading={starting} onClick={() => startCheckout(checkout.tier)}>
              {starting ? t('checkout_opening') : t('checkout_continue')}
            </Button>
          </>
        )}
      </Modal>
    </section>
  );
}

/**
 * Localised feature bullets for a tier.
 *
 * GET /api/web/pricing returns English-only feature text, so a Russian user
 * would otherwise get a Russian page with English bullets. The Mini App already
 * ships translated equivalents (`subscription_info_basic` / `_premium`, newline
 * separated), so we prefer those and fall back to whatever the API sent.
 *
 * Only the DESCRIPTION is localised here. The price always comes from the
 * server, untouched.
 */
function tierFeatures(tierData, t) {
  const key = `subscription_info_${tierData.tier}`;
  const translated = t(key);

  // t() returns the key itself when there's no entry for it.
  if (translated && translated !== key) {
    return translated
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
  }
  return tierData.features ?? [];
}

/** Display a server-provided amount. Formatting only — never arithmetic. */
function formatPrice(amount, currency = 'usd') {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(value);
  } catch {
    return `$${value.toFixed(2)}`;
  }
}
